import { safeParseJson } from '../shared/util/helper.util';
import { getTimelineEventDate, PatientAppTimelineEvent } from './patient-app-timeline.util';

// Actions a clinic can take on an existing patient app enrolment from the patient header.
// The rules mirror PAMP's shared enrollment-status module (canResendAppInvitation and
// isReEnrollmentEligible) so both portals offer the same actions for the same patient.
export type PatientAppActionId = 'resendInvite' | 'quickReEnroll' | 'customReEnroll';

export interface PatientAppAction {
  id: PatientAppActionId;
  labelKey: string;
}

interface AssessmentHistoryItem {
  id?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AssessmentSummary {
  id?: string;
  createdAt?: string;
}

// Shape of the JSON PAMP stores in Patient.patientAppEnrollmentConfig, limited to what the rules read.
interface PatientAppEnrollmentConfig {
  status?: string | null;
  timeline?: PatientAppTimelineEvent[] | null;
  assessmentHistory?: Array<AssessmentHistoryItem | null> | null;
}

// The slice of a patient these rules read. Structural on purpose: the generated GraphQL Patient
// type carries an assessments connection without items, while the profile query adds them.
export interface PatientAppActionsInput {
  patientAppId?: string | null;
  patientAppEnrollmentConfig?: string | PatientAppEnrollmentConfig | null;
  assessments?: { items?: Array<AssessmentSummary | null> | null } | null;
}

function parseEnrollmentConfig(
  patient: PatientAppActionsInput
): PatientAppEnrollmentConfig | null {
  const enrollmentConfig: PatientAppEnrollmentConfig | null = safeParseJson(
    patient.patientAppEnrollmentConfig
  );
  return enrollmentConfig || null;
}

const RESEND_INVITE_ACTION: PatientAppAction = {
  id: 'resendInvite',
  labelKey: 'Resend Invitation'
};

// Same labels as the assessment page's re-enroll menu, so the two read as one feature.
const RE_ENROLL_ACTIONS: PatientAppAction[] = [
  { id: 'quickReEnroll', labelKey: 'Quick re-enroll' },
  { id: 'customReEnroll', labelKey: 'Custom re-enroll' }
];

// PAMP also records invites under these names; the header must recognise all of them.
const INVITED_EVENTS = ['invited', 're-invited', 'reinvited'];

function toTime(dateString: string | null | undefined): number {
  const time = dateString ? new Date(dateString).getTime() : NaN;
  return isNaN(time) ? 0 : time;
}

function normalizeStatus(status: string | null | undefined): string {
  return status ? String(status).trim().toLowerCase() : '';
}

function hasEverBeenInvited(
  patientAppId: string | null,
  status: string,
  timeline: PatientAppTimelineEvent[]
): boolean {
  if (getTimelineEventDate(timeline, INVITED_EVENTS)) {
    return true;
  }
  if (status && status !== 'not enrolled') {
    return true;
  }
  return !!patientAppId;
}

// Resending only makes sense when there is an enrolment code to put in the email, and the
// patient has been invited at least once. Any status past 'Not Enrolled' qualifies, including
// patients who are active or logged out: PAMP offers the same and a duplicate email is harmless.
export function canResendInvitation(patient: PatientAppActionsInput | null | undefined): boolean {
  if (!patient) {
    return false;
  }
  const patientAppId = patient.patientAppId ? patient.patientAppId.trim() : '';
  if (!patientAppId) {
    return false;
  }
  const enrollmentConfig = parseEnrollmentConfig(patient);
  const status = normalizeStatus(enrollmentConfig ? enrollmentConfig.status : null);
  const timeline: PatientAppTimelineEvent[] =
    enrollmentConfig && Array.isArray(enrollmentConfig.timeline) ? enrollmentConfig.timeline : [];
  return hasEverBeenInvited(patientAppId, status, timeline);
}

// A patient can be re-enrolled when their newest portal assessment has not been pushed to the
// app yet, i.e. it is missing from assessmentHistory and newer than the 'current' entry there.
// A paused patient can always be re-enrolled: that is how the pause is lifted.
// Unlike the assessment page, which asks the question about the assessment being viewed, the
// header has no assessment in scope and so reads the patient's latest one.
export function isReEnrollmentEligible(
  patient: PatientAppActionsInput | null | undefined
): boolean {
  if (!patient) {
    return false;
  }
  const enrollmentConfig = parseEnrollmentConfig(patient);
  if (!enrollmentConfig) {
    return false;
  }

  const status = normalizeStatus(enrollmentConfig.status);
  if (!status || status === 'not enrolled' || status === 'invited') {
    return false;
  }
  if (status === 'paused') {
    return true;
  }

  const assessments: AssessmentSummary[] =
    patient.assessments && Array.isArray(patient.assessments.items)
      ? patient.assessments.items.filter((assessment): assessment is AssessmentSummary => !!assessment)
      : [];
  if (assessments.length === 0) {
    return false;
  }

  const history: AssessmentHistoryItem[] = Array.isArray(enrollmentConfig.assessmentHistory)
    ? enrollmentConfig.assessmentHistory.filter((item): item is AssessmentHistoryItem => !!item)
    : [];
  if (history.length === 0) {
    return false;
  }

  const latestAssessment = assessments
    .slice()
    .sort((first, second) => toTime(second.createdAt) - toTime(first.createdAt))[0];
  if (!latestAssessment || !latestAssessment.id) {
    return false;
  }
  if (history.some(item => item.id === latestAssessment.id)) {
    return false;
  }

  const currentHistoryItem = history.find(item => item.status === 'current');
  if (!currentHistoryItem) {
    return true;
  }

  let currentCreatedAt = currentHistoryItem.createdAt || null;
  if (!currentCreatedAt) {
    const currentAssessment = assessments.find(
      assessment => assessment.id === currentHistoryItem.id
    );
    currentCreatedAt = currentAssessment ? currentAssessment.createdAt || null : null;
  }

  return !!latestAssessment.createdAt && toTime(latestAssessment.createdAt) > toTime(currentCreatedAt);
}

// Something the clinic must know about the enrolment before acting on it. Shown as a warning icon
// next to the Actions menu; the message names the action that clears it.
export type PatientAppAdvisoryId = 'paused' | 'invitationExpired';

export interface PatientAppAdvisory {
  id: PatientAppAdvisoryId;
  messageKey: string;
}

const ADVISORY_BY_STATUS: { [status: string]: PatientAppAdvisory } = {
  paused: { id: 'paused', messageKey: 'patientAppAdvisory.paused' },
  'invitation expired': {
    id: 'invitationExpired',
    messageKey: 'patientAppAdvisory.invitationExpired'
  },
  expired: { id: 'invitationExpired', messageKey: 'patientAppAdvisory.invitationExpired' }
};

// Only states that block the patient or the clinic get an advisory: a pause freezes the app until
// re-enrolment, and an expired invite means the patient can no longer join without a resend.
export function buildPatientAppAdvisory(
  patient: PatientAppActionsInput | null | undefined
): PatientAppAdvisory | null {
  if (!patient || !patient.patientAppId) {
    return null;
  }
  const enrollmentConfig = parseEnrollmentConfig(patient);
  const status = normalizeStatus(enrollmentConfig ? enrollmentConfig.status : null);
  return ADVISORY_BY_STATUS[status] || null;
}

// Ordered list for the header's Actions menu. Empty when the patient has no enrolment, which is
// the caller's cue to hide the menu altogether.
export function buildPatientAppActions(
  patient: PatientAppActionsInput | null | undefined
): PatientAppAction[] {
  if (!patient || !patient.patientAppId) {
    return [];
  }
  const actions: PatientAppAction[] = [];
  if (canResendInvitation(patient)) {
    actions.push(RESEND_INVITE_ACTION);
  }
  if (isReEnrollmentEligible(patient)) {
    actions.push(...RE_ENROLL_ACTIONS);
  }
  return actions;
}
